import {isEmpty} from 'lodash-es';
import React from 'react';

import {Button, CheckBox, Input, SpinningLoader, Tabs} from '@/components';
import Modal from '@/components/Modals';
import type {AfriexProcessor} from '@/types';
import {logger} from '@/utils/logger';

import ProcessorCostsTab from './ProcessorCostsTab';
import {ProcessorActionType} from './types';
import useProcessorUpdate from './useProcessorUpdate';

interface ProcessorFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: AfriexProcessor;
}

const ProcessorFormModal = ({
  isOpen,
  onClose,
  initialData,
}: ProcessorFormModalProps) => {
  const {dispatch, isLoading, handleSubmit, processor} = useProcessorUpdate(
    onClose,
    initialData,
  );

  const {code, name, meta} = processor;
  const flags = meta?.flags ?? {};
  const updateLabel = 'Update Processor';
  const submitLabel = 'Update';
  const isDisabled =
    isEmpty(processor?.name) || isEmpty(processor?.code) || isLoading;
  const isExisting = !isEmpty(initialData) && Boolean(initialData?.id);

  const generalTabContent = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        // `handleSubmit` already reports a failed processor save to the admin
        // through showErrorMessage, so this only fires if that reporting itself
        // threw. It goes to the dev logger rather than a second toast: a toast
        // is exactly what just failed, and trying again would only throw again.
        handleSubmit(processor).catch(logger.error);
      }}
      className="flex flex-col px-2"
    >
      <Input
        label="Name"
        placeholder="Name of Processor"
        value={name || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          dispatch({
            type: ProcessorActionType.UPDATE_PROCESSOR,
            payload: {
              name: e.target?.value,
            },
          })
        }
        className="w-full mb-3"
      />

      {/* Read-only: the record is matched to its processor by code. */}
      <Input
        label="Code"
        placeholder="Processor Code (e.g. STRIPE)"
        value={code || ''}
        disabled
        readOnly
        className="w-full mb-3"
      />

      <hr className="my-4 dark:border-dark-borderMid" />

      <div className="space-y-3">
        <CheckBox
          label="Active"
          type="checkbox"
          checked={Boolean(flags?.isActive)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({
              type: ProcessorActionType.UPDATE_FLAGS,
              payload: {
                isActive: e.target?.checked,
              },
            })
          }
        />

        <CheckBox
          label="Funded"
          type="checkbox"
          checked={Boolean(flags?.isFunded)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({
              type: ProcessorActionType.UPDATE_FLAGS,
              payload: {
                isFunded: e.target?.checked,
              },
            })
          }
        />

        <CheckBox
          label="Deactivated"
          type="checkbox"
          checked={Boolean(flags?.isDeactivated)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({
              type: ProcessorActionType.UPDATE_FLAGS,
              payload: {
                isDeactivated: e.target?.checked,
              },
            })
          }
        />

        <CheckBox
          label="Down"
          type="checkbox"
          checked={Boolean(flags?.isDown)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({
              type: ProcessorActionType.UPDATE_FLAGS,
              payload: {
                isDown: e.target?.checked,
              },
            })
          }
        />
      </div>

      <hr className="my-4 dark:border-dark-borderMid" />

      <div className="text-end pb-5">
        <Button
          disabled={isDisabled}
          type="submit"
          variant="solid"
          colorScheme="cyan"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );

  const costsTabContent = isExisting ? (
    <ProcessorCostsTab
      processorCode={initialData?.code}
      costs={initialData?.costs ?? []}
    />
  ) : (
    <p className="text-sm text-slate-400 italic py-4">
      Save the processor first before managing costs.
    </p>
  );

  const tabs = [
    {
      id: 'general',
      title: 'General',
      content: generalTabContent,
    },
    {
      id: 'costs',
      title: 'Costs',
      content: costsTabContent,
    },
  ];

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width={'w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0'}
        closeOnOutsideClick
        title={updateLabel}
      >
        <div className="overflow-hidden">
          {isLoading ? (
            <div className="flex w-full h-full justify-center items-center my-5">
              <SpinningLoader />
            </div>
          ) : (
            <Tabs tabs={tabs} contentContainerStyle="max-h-96 mb-3" />
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default ProcessorFormModal;
